import { Box, Typography } from '@mui/material';
import { useId } from 'react';
import { tokens } from '../../theme';
import type { VersionCompareProps } from './types';

/**
 * "Compare to", a dropdown of every version but the one on screen.
 *
 * **The whole control is absent for an Agent Skill with one version.** There is nothing to
 * compare it with, and a dropdown with no choices is a promise nothing keeps — so it
 * draws no label and no empty select, not a disabled one.
 *
 * **A native `select`**, because the list is a handful of version numbers and the
 * platform's own control brings the keyboard, the screen reader behaviour and the
 * touch picker for nothing.
 *
 * The version on screen is left out of the options rather than disabled in them: a
 * disabled entry is still an option a reader reads past.
 */
export const VersionCompare = ({ versions, selected, value, onChange }: VersionCompareProps) => {
  const id = useId();
  const others = versions.filter((version) => version !== selected);

  if (versions.length < 2) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography
        component="label"
        htmlFor={id}
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: '1px' }}
      >
        Compare to
      </Typography>

      <Box
        component="select"
        id={id}
        value={value ?? ''}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
          onChange(event.target.value === '' ? null : event.target.value);
        }}
        sx={{
          fontFamily: tokens.fontFamily.mono,
          fontSize: 13,
          color: 'text.primary',
          backgroundColor: 'surface.raised',
          border: 1,
          borderColor: 'border.strong',
          borderRadius: `${String(tokens.radius.md)}px`,
          paddingInline: 3,
          paddingBlock: 2,
          width: '100%',
        }}
      >
        <option value="">Not comparing</option>
        {others.map((version) => (
          <option key={version} value={version}>
            {version}
          </option>
        ))}
      </Box>
    </Box>
  );
};
